import React, { useState, useEffect } from "react"
import { useAppDispatch, useAppSelector } from "../../../../app/hooks"
import { closeVobAssignmentEditModal } from "../../../../app/slices/ProfileModuleSlice";
import { Profile } from "../../../../services/profileService/model/profile/Profile";
import { Alert, AlertTitle, Button, Dialog, DialogActions, DialogContent, DialogTitle, Stack, TextField, Autocomplete, Chip } from "@mui/material";
import { useUpdateVobAssignmentMutation, useAllProfilesQuery } from "../../../../services/profileService/profile-api";
import { VobAssignmentUpdateCommand } from "../../../../services/profileService/commands";
import { usePayerListQuery } from "../../../../services/orgService/org-api";
import CenterList from "../../../../services/orgService/model/CenterList";
import { findConflictingAssignments, ConflictingAssignment } from "./vobAssignmentUtils";

type ownProps = {
    profile: Profile;
    centers: CenterList[];
}

function VobAssignmentEdit(props: ownProps) {
    const { modalOpen, assignment } = useAppSelector((state) => state.profileModule.vobAssignmentEditModal);
    const dispatch = useAppDispatch();
    const [updateVobAssignment] = useUpdateVobAssignmentMutation();
    const { data: payers } = usePayerListQuery('ALL');
    const { data: allProfiles } = useAllProfilesQuery();

    const [selectedValues, setSelectedValues] = useState<string[]>([]);
    const [isSaving, setIsSaving] = useState(false);
    const [conflicts, setConflicts] = useState<ConflictingAssignment[]>([]);

    useEffect(() => {
        if (assignment) {
            setSelectedValues(assignment.assignmentValues || []);
        }
    }, [assignment]);

    // Check for conflicting assignments when values change
    useEffect(() => {
        if (assignment && selectedValues.length > 0) {
            const foundConflicts = findConflictingAssignments(
                allProfiles,
                assignment.assignmentTypeCode,
                assignment.processTypeCode,
                selectedValues,
                props.profile.upn  // Exclude current user
            );
            setConflicts(foundConflicts);
        } else {
            setConflicts([]);
        }
    }, [allProfiles, assignment, selectedValues, props.profile.upn]);

    const uniqueFinancialClasses = Array.from(
        new Set(payers?.map(p => p.financialClass).filter(Boolean))
    ).sort();

    const getValueOptions = () => {
        if (!assignment) return [];

        if (assignment.assignmentTypeCode === 'Center') {
            return props.centers.map(c => ({ value: c.code, label: `${c.code} - ${c.name}` }));
        } else if (assignment.assignmentTypeCode === 'Payer') {
            return uniqueFinancialClasses.map(fc => ({ value: fc, label: fc }));
        }
        return [];
    }

    const handleSave = async () => {
        if (!assignment || selectedValues.length === 0) {
            return;
        }

        setIsSaving(true);

        try {
            // Atomic update - removes existing values and adds new ones in a single transaction
            const updateCommand = new VobAssignmentUpdateCommand(
                props.profile.upn,
                assignment.assignmentTypeCode,
                assignment.processTypeCode,
                selectedValues
            );

            await updateVobAssignment(updateCommand).unwrap();
            handleClose();
        } catch (error) {
            console.error('Failed to update assignment:', error);
        } finally {
            setIsSaving(false);
        }
    }

    const handleClose = () => {
        setSelectedValues([]);
        setIsSaving(false);
        setConflicts([]);
        dispatch(closeVobAssignmentEditModal());
    }

    const getProfileDisplayName = (): string => {
        const profile = props.profile;
        if (profile.identity && profile.identity.trim() !== '') {
            return profile.identity;
        }
        if (profile.fullName && profile.fullName.trim() !== '' && profile.fullName !== ', ') {
            return `${profile.fullName} (${profile.upn})`;
        }
        return profile.email || profile.upn;
    };

    return <React.Fragment>
        <Dialog open={modalOpen} maxWidth='md' fullWidth>
            <DialogTitle>Edit VOB Assignment</DialogTitle>
            <DialogContent>
                <Stack spacing={3} sx={{ mt: 2 }}>
                    <TextField
                        label="User"
                        value={getProfileDisplayName()}
                        fullWidth
                        disabled
                    />

                    <TextField
                        label="Assignment Type"
                        value={assignment?.assignmentTypeName || ''}
                        fullWidth
                        disabled
                    />

                    <TextField
                        label="Process Type"
                        value={assignment?.processTypeName || ''}
                        fullWidth
                        disabled
                    />

                    {assignment && (
                        <Autocomplete
                            multiple
                            options={getValueOptions()}
                            getOptionLabel={(option) => option.label}
                            value={getValueOptions().filter(opt => selectedValues.includes(opt.value))}
                            onChange={(event, values) => setSelectedValues(values.map(v => v.value))}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    label={assignment.assignmentTypeCode === 'Center' ? 'Select Centers' : 'Select Payers'}
                                    variant="outlined"
                                />
                            )}
                            renderTags={(value, getTagProps) =>
                                value.map((option, index) => (
                                    <Chip label={option.label} {...getTagProps({ index })} />
                                ))
                            }
                            fullWidth
                        />
                    )}

                    {conflicts.length > 0 && (
                        <Alert severity="warning">
                            <AlertTitle>Assignment Conflicts</AlertTitle>
                            {conflicts.map((c, i) => (
                                <div key={i}>'{c.userName}' is currently assigned to '{c.value}' for '{assignment?.assignmentTypeName}'</div>
                            ))}
                        </Alert>
                    )}
                </Stack>
            </DialogContent>
            <DialogActions>
                <Stack direction='row' justifyContent='flex-end' spacing={2}>
                    <Button variant='outlined' onClick={handleClose} disabled={isSaving}>Cancel</Button>
                    <Button
                        variant='contained'
                        onClick={handleSave}
                        disabled={selectedValues.length === 0 || isSaving}
                    >
                        {isSaving ? 'Saving...' : 'Save'}
                    </Button>
                </Stack>
            </DialogActions>
        </Dialog>
    </React.Fragment>
}

export default VobAssignmentEdit;
